import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import myConfig from "../configs/config";
import { isObsExpired } from "../containers/Operation/ScheduledBadge";

const PendingSchedulesContext = createContext({
  pendingObs: [],
  completedObs: [],
  loading: false,
  completedLoading: false,
  error: null,
  completedError: null,
  refreshPending: () => {},
  refreshCompleted: () => {},
});

export function usePendingSchedules() {
  return useContext(PendingSchedulesContext);
}

function enrichObservation(obs) {
  return {
    ...obs,
    operationId: obs.operation,
    operationName: obs.operation_name || "",
  };
}

export function PendingSchedulesProvider({ farm_id, children }) {
  const [pendingObs, setPendingObs] = useState([]);
  const [completedObs, setCompletedObs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [error, setError] = useState(null);
  const [completedError, setCompletedError] = useState(null);

  const fetchPendingObservations = useCallback(async () => {
    if (!farm_id) return;

    setLoading(true);
    setError(null);

    try {
      const res = await axios.get(
        `${myConfig.API_URL}/observation/pending/?farm_id=${farm_id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          },
        }
      );

      const data = (res.data || []).map(enrichObservation);

      data.sort((a, b) => {
        const aExpired = isObsExpired(a);
        const bExpired = isObsExpired(b);
        if (aExpired && !bExpired) return -1;
        if (!aExpired && bExpired) return 1;
        return new Date(a.start_date) - new Date(b.start_date);
      });

      setPendingObs(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [farm_id]);

  const fetchCompletedObservations = useCallback(async () => {
    if (!farm_id) return;

    setCompletedLoading(true);
    setCompletedError(null);

    try {
      const res = await axios.get(
        `${myConfig.API_URL}/observation/completed/?farm_id=${farm_id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          },
        }
      );

      const data = (res.data || []).map(enrichObservation);
      setCompletedObs(data);
    } catch (err) {
      setCompletedError(err);
    } finally {
      setCompletedLoading(false);
    }
  }, [farm_id]);

  useEffect(() => {
    fetchPendingObservations();
    fetchCompletedObservations();
  }, [fetchPendingObservations, fetchCompletedObservations]);

  const refreshAll = useCallback(() => {
    fetchPendingObservations();
    fetchCompletedObservations();
  }, [fetchPendingObservations, fetchCompletedObservations]);

  return (
    <PendingSchedulesContext.Provider
      value={{
        pendingObs,
        completedObs,
        loading,
        completedLoading,
        error,
        completedError,
        refreshPending: refreshAll,
        refreshCompleted: fetchCompletedObservations,
      }}
    >
      {children}
    </PendingSchedulesContext.Provider>
  );
}
